/**
 * ループ回数スライダー（layout-and-navigation.md §6）。範囲 10〜200,000 の対数刻み。
 * スナップ点は 10, 100, 1,000, 10,000, 100,000, 200,000。
 */
import type { MeterDepth } from '../../lib/router';

export const SLIDER_MIN_N = 10;
export const SLIDER_MAX_N = 200_000;
/** range input の目盛り数（0〜POSITIONS）。 */
export const SLIDER_POSITIONS = 1000;
export const SNAP_POINTS: readonly number[] = [10, 100, 1_000, 10_000, 100_000, 200_000];

const LO = Math.log10(SLIDER_MIN_N);
const HI = Math.log10(SLIDER_MAX_N);

/** 位置 → N（10・100・1,000 単位に丸め、スナップ点の 4% 以内は吸い寄せる）。 */
export function positionToN(pos: number): number {
  const p = Math.min(SLIDER_POSITIONS, Math.max(0, pos));
  let n = 10 ** (LO + (p / SLIDER_POSITIONS) * (HI - LO));
  const unit = n >= 1000 ? 1000 : n >= 100 ? 100 : 10;
  n = Math.round(n / unit) * unit;
  for (const s of SNAP_POINTS) if (Math.abs(n - s) / s < 0.04) n = s;
  return Math.min(SLIDER_MAX_N, Math.max(SLIDER_MIN_N, n));
}

/** N → 位置（範囲外は端に寄せる）。 */
export function nToPosition(n: number): number {
  const c = Math.min(SLIDER_MAX_N, Math.max(SLIDER_MIN_N, n));
  return Math.round(((Math.log10(c) - LO) / (HI - LO)) * SLIDER_POSITIONS);
}

/** 次のスナップ点（dir = 1 で大きい方）。無ければ null。 */
export function nextSnap(n: number, dir: 1 | -1): number | null {
  const list = dir === 1 ? SNAP_POINTS.filter((s) => s > n) : SNAP_POINTS.filter((s) => s < n);
  const v = dir === 1 ? list[0] : list[list.length - 1];
  return v ?? null;
}

/** スナップ点の目盛りの位置（0〜100%）。 */
export function snapPercent(n: number): number {
  return (nToPosition(n) / SLIDER_POSITIONS) * 100;
}

export interface DepthPreset {
  id: MeterDepth;
  /** 表とセレクトの短い名前。 */
  short: string;
  label: string;
  ops: string;
}

/** ループの重なり（Meter.svelte の `?depth` と同じ値）。 */
const DOUBLE: DepthPreset = { id: 2, short: '2 重', label: '2 重（N × N 回）', ops: 'N^2' };

export const DEPTH_PRESETS: readonly DepthPreset[] = [
  { id: 1, short: '1 重', label: '1 重（N 回）', ops: 'N' },
  { id: 'nlogn', short: 'N log N', label: 'N × 何回半分にできるか（N log N）', ops: 'N*log2(N)' },
  DOUBLE,
  { id: 3, short: '3 重', label: '3 重（N × N × N 回）', ops: 'N^3' },
];

export function depthPreset(id: MeterDepth): DepthPreset {
  return DEPTH_PRESETS.find((d) => d.id === id) ?? DOUBLE;
}
